import {
  Promotion,
  Quest,
  QuestTargetExpanded,
  AgeRange,
  Keyword,
  getQuestById,
} from "../data";
import {
  ProfileTypeValue,
  TemplateScopeValue,
  GenderValue,
  MaritalStatusValue,
  JoinConditionValue,
  IncentiveTypeValue,
  LinkParamValue,
  DayOfWeekValue,
} from "@quest_editor/constants";

/**
 * urqlのuseMutationと同じインターフェースを返す型
 */
export type MutationResult<T> = {
  data: T | undefined;
  fetching: boolean;
  error: Error | null;
};

// ======================
// Quest Mutations
// ======================

export type CreateQuestInput = {
  promotionId: string;
  name: string;
  description: string;
  note?: string;
  // 報酬
  incentiveType?: IncentiveTypeValue;
  tamaruPoint?: number | null;
  prizeName?: string | null;
  // 画像
  imageUrl?: string;
  popupImageUrl?: string;
  // 期間
  startAt?: string | null;
  endAt?: string | null;
  // ターゲット
  targetClearCount?: number;
  // レシート関連
  productCheckNotice?: string;
  checkerNote?: string;
  receiptCheckDescription?: string;
  checkerPhoneFile?: string;
  // 運用
  slackChannel?: string;
  memo?: string;
  // 外部リンク
  linkTitle?: string;
  linkUrl?: string;
  linkParams?: LinkParamValue[];
  // アンケート
  surveyId?: string | null;
  surveyPreInstruction?: string;
  // 動的パラメータ
  qParam1Text?: string;
  qParam2Text?: string;
  qParam3Text?: string;
};

export type CreateQuestData = {
  createQuest: Quest;
};

export type CreateQuestResult = {
  createQuest: (
    input: CreateQuestInput,
  ) => Promise<MutationResult<CreateQuestData>>;
  fetching: boolean;
  error: Error | null;
};

let questIdCounter = 1000;

export const useCreateQuest = (): CreateQuestResult => {
  const createQuest = async (
    input: CreateQuestInput,
  ): Promise<MutationResult<CreateQuestData>> => {
    // モック: inputで渡された値のみを使用してクエストを作成
    // 実際のAPIでは、サーバー側でid, status, createStatus, progress, progressMax, hashが設定される
    const newId = String(++questIdCounter);
    const newQuest: Quest = {
      id: newId,
      promotionId: input.promotionId,
      name: input.name,
      description: input.description,
      status: "created",
      createStatus: "waiting",
      progress: null,
      progressMax: input.targetClearCount ?? 0,
      incentiveType: input.incentiveType ?? "tamaru",
      tamaruPoint: input.tamaruPoint ?? null,
      prizeName: input.prizeName ?? null,
      reward: input.tamaruPoint ? `${input.tamaruPoint}pt` : "100pt",
      imageUrl: input.imageUrl ?? "",
      startAt: input.startAt ?? null,
      endAt: input.endAt ?? null,
      targetClearCount: input.targetClearCount ?? 0,
      estimateRequested: false,
      hash: `quest_hash_${newId}`,
      note: input.note,
      popupImageUrl: input.popupImageUrl,
      productCheckNotice: input.productCheckNotice,
      checkerNote: input.checkerNote,
      receiptCheckDescription: input.receiptCheckDescription,
      checkerPhoneFile: input.checkerPhoneFile,
      slackChannel: input.slackChannel,
      memo: input.memo,
      linkTitle: input.linkTitle,
      linkUrl: input.linkUrl,
      linkParams: input.linkParams,
      surveyId: input.surveyId,
      surveyPreInstruction: input.surveyPreInstruction,
      qParam1Text: input.qParam1Text,
      qParam2Text: input.qParam2Text,
      qParam3Text: input.qParam3Text,
    };

    return {
      data: { createQuest: newQuest },
      fetching: false,
      error: null,
    };
  };

  return { createQuest, fetching: false, error: null };
};

export type UpdateQuestInput = {
  id: string;
  name?: string;
  description?: string;
  note?: string;
  // 報酬
  incentiveType?: IncentiveTypeValue;
  tamaruPoint?: number | null;
  prizeName?: string | null;
  // 画像
  imageUrl?: string;
  popupImageUrl?: string;
  // 期間
  startAt?: string | null;
  endAt?: string | null;
  // ターゲット
  targetClearCount?: number;
  // レシート関連
  productCheckNotice?: string;
  checkerNote?: string;
  receiptCheckDescription?: string;
  checkerPhoneFile?: string;
  // 運用
  slackChannel?: string;
  memo?: string;
  // 外部リンク
  linkTitle?: string;
  linkUrl?: string;
  linkParams?: LinkParamValue[];
  // アンケート
  surveyId?: string | null;
  surveyPreInstruction?: string;
  // 動的パラメータ
  qParam1Text?: string;
  qParam2Text?: string;
  qParam3Text?: string;
};

export type UpdateQuestData = {
  updateQuest: Quest;
};

export type UpdateQuestResult = {
  updateQuest: (
    input: UpdateQuestInput,
  ) => Promise<MutationResult<UpdateQuestData>>;
  fetching: boolean;
  error: Error | null;
};

export const useUpdateQuest = (): UpdateQuestResult => {
  const updateQuest = async (
    input: UpdateQuestInput,
  ): Promise<MutationResult<UpdateQuestData>> => {
    // モック: inputで渡されたフィールドのみを使用
    // 実際のAPIでは既存データとマージされ、渡されなかったフィールドは既存値が保持される
    // このモックでは簡略化のため、inputの値をそのまま返す
    const updatedQuest: Quest = {
      id: input.id,
      promotionId: "1",
      status: "created",
      createStatus: "waiting",
      progress: null,
      progressMax: input.targetClearCount ?? 0,
      hash: `quest_hash_${input.id}`,
      name: input.name ?? "",
      description: input.description ?? "",
      incentiveType: input.incentiveType ?? "tamaru",
      tamaruPoint: input.tamaruPoint ?? null,
      prizeName: input.prizeName ?? null,
      reward: input.tamaruPoint ? `${input.tamaruPoint}pt` : "100pt",
      imageUrl: input.imageUrl ?? "",
      startAt: input.startAt ?? null,
      endAt: input.endAt ?? null,
      targetClearCount: input.targetClearCount ?? 0,
      estimateRequested: false,
      note: input.note,
      popupImageUrl: input.popupImageUrl,
      productCheckNotice: input.productCheckNotice,
      checkerNote: input.checkerNote,
      receiptCheckDescription: input.receiptCheckDescription,
      checkerPhoneFile: input.checkerPhoneFile,
      slackChannel: input.slackChannel,
      memo: input.memo,
      linkTitle: input.linkTitle,
      linkUrl: input.linkUrl,
      linkParams: input.linkParams,
      surveyId: input.surveyId,
      surveyPreInstruction: input.surveyPreInstruction,
      qParam1Text: input.qParam1Text,
      qParam2Text: input.qParam2Text,
      qParam3Text: input.qParam3Text,
    };

    return {
      data: { updateQuest: updatedQuest },
      fetching: false,
      error: null,
    };
  };

  return { updateQuest, fetching: false, error: null };
};

export type DeleteQuestInput = {
  id: string;
};

export type DeleteQuestData = {
  deleteQuest: { id: string };
};

export type DeleteQuestResult = {
  deleteQuest: (
    input: DeleteQuestInput,
  ) => Promise<MutationResult<DeleteQuestData>>;
  fetching: boolean;
  error: Error | null;
};

export const useDeleteQuest = (): DeleteQuestResult => {
  const deleteQuest = async (
    input: DeleteQuestInput,
  ): Promise<MutationResult<DeleteQuestData>> => {
    return {
      data: { deleteQuest: { id: input.id } },
      fetching: false,
      error: null,
    };
  };

  return { deleteQuest, fetching: false, error: null };
};

export type DuplicateQuestInput = {
  id: string;
  newName: string;
};

export type DuplicateQuestData = {
  duplicateQuest: Quest;
};

export type DuplicateQuestResult = {
  duplicateQuest: (
    input: DuplicateQuestInput,
  ) => Promise<MutationResult<DuplicateQuestData>>;
  fetching: boolean;
  error: Error | null;
};

export const useDuplicateQuest = (): DuplicateQuestResult => {
  const duplicateQuest = async (
    input: DuplicateQuestInput,
  ): Promise<MutationResult<DuplicateQuestData>> => {
    // 元のクエストを取得
    const originalQuest = getQuestById(input.id);
    if (!originalQuest) {
      return {
        data: undefined,
        fetching: false,
        error: new Error("Quest not found"),
      };
    }

    // 元のクエストをコピーして新しいIDを付与
    const newId = String(++questIdCounter);
    const newQuest: Quest = {
      ...originalQuest,
      id: newId,
      name: input.newName,
      status: "created",
      createStatus: "waiting",
      progress: null,
      hash: `quest_hash_${newId}`,
      startAt: null,
      endAt: null,
      estimateRequested: false,
    };

    return {
      data: { duplicateQuest: newQuest },
      fetching: false,
      error: null,
    };
  };

  return { duplicateQuest, fetching: false, error: null };
};

// ======================
// QuestTarget Mutations
// ======================

export type UpdateQuestTargetInput = {
  questId: string;
  genders: GenderValue[];
  ageRanges: AgeRange[];
  prefectureIds: string[];
  maritalStatuses: MaritalStatusValue[];
  elapsedDays: string;
  joinCondition: JoinConditionValue;
  purchaseChannelIds: string[];
  storeIds: string[];
  productIds: string[];
  categoryIds: string[];
  manufacturerIds: string[];
  keywords: Keyword[];
  purchaseDateFrom: string;
  purchaseDateTo: string;
  purchaseFrequency: number;
  clearedQuestId: string;
  unclearedQuestId: string;
  unparticipatedQuestId: string;
  targetSegmentIds: string[];
  excludeSegmentIds: string[];
  targetClearCount: number;
  divisions: number[];
  eligibleCount: number;
  deliveryNumber: number;
};

export type UpdateQuestTargetData = {
  updateQuestTarget: QuestTargetExpanded;
};

export type UpdateQuestTargetResult = {
  updateQuestTarget: (
    input: UpdateQuestTargetInput,
  ) => Promise<MutationResult<UpdateQuestTargetData>>;
  fetching: boolean;
  error: Error | null;
};

export const useUpdateQuestTarget = (): UpdateQuestTargetResult => {
  const updateQuestTarget = async (
    input: UpdateQuestTargetInput,
  ): Promise<MutationResult<UpdateQuestTargetData>> => {
    // モック: 更新されたQuestTargetを返す
    const updatedQuestTarget: QuestTargetExpanded = {
      id: "1",
      questId: input.questId,
      genders: input.genders,
      maritalStatuses: input.maritalStatuses,
      elapsedDays: input.elapsedDays,
      joinCondition: input.joinCondition,
      purchaseDateFrom: input.purchaseDateFrom,
      purchaseDateTo: input.purchaseDateTo,
      purchaseFrequency: input.purchaseFrequency,
      clearedQuestId: input.clearedQuestId,
      unclearedQuestId: input.unclearedQuestId,
      unparticipatedQuestId: input.unparticipatedQuestId,
      divisions: input.divisions,
      eligibleCount: input.eligibleCount,
      deliveryNumber: input.deliveryNumber,
      ageRanges: input.ageRanges,
      keywords: input.keywords,
      prefectures: [],
      purchaseChannels: [],
      stores: [],
      products: [],
      categories: [],
      manufacturers: [],
      targetSegments: [],
      excludeSegments: [],
    };

    return {
      data: { updateQuestTarget: updatedQuestTarget },
      fetching: false,
      error: null,
    };
  };

  return { updateQuestTarget, fetching: false, error: null };
};

// ======================
// Promotion Mutations
// ======================

export type CreatePromotionInput = {
  name: string;
  type: string;
  description: string;
  options: {
    dataCheck: boolean;
    dataCheckPriority: boolean;
    profiles: ProfileTypeValue[];
    template: boolean;
    templateScope?: TemplateScopeValue;
  };
};

export type CreatePromotionData = {
  createPromotion: Promotion;
};

export type CreatePromotionResult = {
  createPromotion: (
    input: CreatePromotionInput,
  ) => Promise<MutationResult<CreatePromotionData>>;
  fetching: boolean;
  error: Error | null;
};

let promotionIdCounter = 1000;

export const useCreatePromotion = (): CreatePromotionResult => {
  const createPromotion = async (
    input: CreatePromotionInput,
  ): Promise<MutationResult<CreatePromotionData>> => {
    const newPromotion: Promotion = {
      id: String(++promotionIdCounter),
      name: input.name,
      type: input.type as Promotion["type"],
      status: "created",
      owner: "own",
      description: input.description,
      options: input.options,
      returnUrl: `https://example.com/promotion/${promotionIdCounter}`,
    };

    return {
      data: { createPromotion: newPromotion },
      fetching: false,
      error: null,
    };
  };

  return { createPromotion, fetching: false, error: null };
};

export type UpdatePromotionInput = {
  id: string;
  name?: string;
  description?: string;
  options?: {
    dataCheck: boolean;
    dataCheckPriority: boolean;
    profiles: ProfileTypeValue[];
    template: boolean;
    templateScope?: TemplateScopeValue;
  };
};

export type UpdatePromotionData = {
  updatePromotion: Promotion;
};

export type UpdatePromotionResult = {
  updatePromotion: (
    input: UpdatePromotionInput,
  ) => Promise<MutationResult<UpdatePromotionData>>;
  fetching: boolean;
  error: Error | null;
};

export const useUpdatePromotion = (): UpdatePromotionResult => {
  const updatePromotion = async (
    input: UpdatePromotionInput,
  ): Promise<MutationResult<UpdatePromotionData>> => {
    const updatedPromotion: Promotion = {
      id: input.id,
      name: input.name ?? "",
      type: "qa",
      status: "created",
      owner: "own",
      description: input.description ?? "",
      options: input.options ?? {
        dataCheck: false,
        dataCheckPriority: false,
        profiles: [],
        template: false,
      },
      returnUrl: `https://example.com/promotion/${input.id}`,
    };

    return {
      data: { updatePromotion: updatedPromotion },
      fetching: false,
      error: null,
    };
  };

  return { updatePromotion, fetching: false, error: null };
};

export type DeletePromotionInput = {
  id: string;
};

export type DeletePromotionData = {
  deletePromotion: { id: string };
};

export type DeletePromotionResult = {
  deletePromotion: (
    input: DeletePromotionInput,
  ) => Promise<MutationResult<DeletePromotionData>>;
  fetching: boolean;
  error: Error | null;
};

export const useDeletePromotion = (): DeletePromotionResult => {
  const deletePromotion = async (
    input: DeletePromotionInput,
  ): Promise<MutationResult<DeletePromotionData>> => {
    return {
      data: { deletePromotion: { id: input.id } },
      fetching: false,
      error: null,
    };
  };

  return { deletePromotion, fetching: false, error: null };
};

export type DuplicatePromotionInput = {
  id: string;
  newName: string;
};

export type DuplicatePromotionData = {
  duplicatePromotion: Promotion;
};

export type DuplicatePromotionResult = {
  duplicatePromotion: (
    input: DuplicatePromotionInput,
  ) => Promise<MutationResult<DuplicatePromotionData>>;
  fetching: boolean;
  error: Error | null;
};

export const useDuplicatePromotion = (): DuplicatePromotionResult => {
  const duplicatePromotion = async (
    input: DuplicatePromotionInput,
  ): Promise<MutationResult<DuplicatePromotionData>> => {
    const newPromotion: Promotion = {
      id: String(++promotionIdCounter),
      name: input.newName,
      type: "qa",
      status: "created",
      owner: "own",
      description: "",
      options: {
        dataCheck: false,
        dataCheckPriority: false,
        profiles: [],
        template: false,
      },
      returnUrl: `https://example.com/promotion/${promotionIdCounter}`,
    };

    return {
      data: { duplicatePromotion: newPromotion },
      fetching: false,
      error: null,
    };
  };

  return { duplicatePromotion, fetching: false, error: null };
};

// ======================
// Quest Start Mutations
// ======================

export type RequestQuestStartInput = {
  questId: string;
};

export type RequestQuestStartData = {
  requestQuestStart: { questId: string; status: string };
};

export type UseRequestQuestStartResult = {
  requestQuestStart: (
    input: RequestQuestStartInput,
  ) => Promise<MutationResult<RequestQuestStartData>>;
  fetching: boolean;
  error: Error | null;
};

export const useRequestQuestStart = (): UseRequestQuestStartResult => {
  const requestQuestStart = async (
    input: RequestQuestStartInput,
  ): Promise<MutationResult<RequestQuestStartData>> => {
    return {
      data: {
        requestQuestStart: { questId: input.questId, status: "request_waiting" },
      },
      fetching: false,
      error: null,
    };
  };

  return { requestQuestStart, fetching: false, error: null };
};

export type ApproveQuestStartInput = {
  questId: string;
};

export type ApproveQuestStartData = {
  approveQuestStart: { questId: string; status: string };
};

export type UseApproveQuestStartResult = {
  approveQuestStart: (
    input: ApproveQuestStartInput,
  ) => Promise<MutationResult<ApproveQuestStartData>>;
  fetching: boolean;
  error: Error | null;
};

export const useApproveQuestStart = (): UseApproveQuestStartResult => {
  const approveQuestStart = async (
    input: ApproveQuestStartInput,
  ): Promise<MutationResult<ApproveQuestStartData>> => {
    return {
      data: {
        approveQuestStart: { questId: input.questId, status: "in_progress" },
      },
      fetching: false,
      error: null,
    };
  };

  return { approveQuestStart, fetching: false, error: null };
};

export type RejectQuestStartInput = {
  questId: string;
};

export type RejectQuestStartData = {
  rejectQuestStart: { questId: string; status: string };
};

export type UseRejectQuestStartResult = {
  rejectQuestStart: (
    input: RejectQuestStartInput,
  ) => Promise<MutationResult<RejectQuestStartData>>;
  fetching: boolean;
  error: Error | null;
};

export const useRejectQuestStart = (): UseRejectQuestStartResult => {
  const rejectQuestStart = async (
    input: RejectQuestStartInput,
  ): Promise<MutationResult<RejectQuestStartData>> => {
    return {
      data: {
        rejectQuestStart: { questId: input.questId, status: "created" },
      },
      fetching: false,
      error: null,
    };
  };

  return { rejectQuestStart, fetching: false, error: null };
};

// ======================
// Quest Estimate Mutations
// ======================

export type RequestEstimateInput = {
  questId: string;
};

export type RequestEstimateData = {
  requestEstimate: { questId: string; status: string };
};

export type UseRequestEstimateResult = {
  requestEstimate: (
    input: RequestEstimateInput,
  ) => Promise<MutationResult<RequestEstimateData>>;
  fetching: boolean;
  error: Error | null;
};

export const useRequestEstimate = (): UseRequestEstimateResult => {
  const requestEstimate = async (
    input: RequestEstimateInput,
  ): Promise<MutationResult<RequestEstimateData>> => {
    return {
      data: {
        requestEstimate: { questId: input.questId, status: "estimate_requested" },
      },
      fetching: false,
      error: null,
    };
  };

  return { requestEstimate, fetching: false, error: null };
};

export type SubmitOrderInput = {
  questId: string;
};

export type SubmitOrderData = {
  submitOrder: { questId: string; status: string };
};

export type UseSubmitOrderResult = {
  submitOrder: (
    input: SubmitOrderInput,
  ) => Promise<MutationResult<SubmitOrderData>>;
  fetching: boolean;
  error: Error | null;
};

export const useSubmitOrder = (): UseSubmitOrderResult => {
  const submitOrder = async (
    input: SubmitOrderInput,
  ): Promise<MutationResult<SubmitOrderData>> => {
    return {
      data: {
        submitOrder: { questId: input.questId, status: "order_submitted" },
      },
      fetching: false,
      error: null,
    };
  };

  return { submitOrder, fetching: false, error: null };
};

// ======================
// Progress Notification Mutations
// ======================

export type NotificationSettingInput = {
  email: string;
  days: DayOfWeekValue[];
};

export type UpdateNotificationSettingsInput = {
  promotionId: string;
  settings: NotificationSettingInput[];
};

export type UpdateNotificationSettingsData = {
  updateNotificationSettings: { success: boolean };
};

export type UseUpdateNotificationSettingsResult = {
  updateNotificationSettings: (
    input: UpdateNotificationSettingsInput,
  ) => Promise<MutationResult<UpdateNotificationSettingsData>>;
  fetching: boolean;
  error: Error | null;
};

export const useUpdateNotificationSettings =
  (): UseUpdateNotificationSettingsResult => {
    const updateNotificationSettings = async (
      _input: UpdateNotificationSettingsInput,
    ): Promise<MutationResult<UpdateNotificationSettingsData>> => {
      return {
        data: {
          updateNotificationSettings: { success: true },
        },
        fetching: false,
        error: null,
      };
    };

    return { updateNotificationSettings, fetching: false, error: null };
  };
